import { Heart, Star, Plus, Minus, Leaf } from 'lucide-react';
import { motion } from 'framer-motion';
import { useState } from 'react';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import { CustomizeSheet } from './CustomizeSheet';

export const FoodCard = ({ item }) => {
  const [fav, setFav] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const { items, updateQty, systemConfig } = useCart();
  const cartItem = items.find((i) => i.item_id === item.id);

  const isOfflineAndNoPreorders = !systemConfig?.is_currently_open && !systemConfig?.allow_preorders;

  const onAdd = () => {
    if (isOfflineAndNoPreorders) {
      toast.error('Kitchen is offline and pre-orders are not allowed at the moment.');
      return;
    }
    setSheetOpen(true);
  };

  const handleQtyChange = (newQty) => {
    if (newQty > (cartItem?.quantity || 0) && isOfflineAndNoPreorders) {
      toast.error('Our kitchen is offline. Cannot add more items.');
      return;
    }
    updateQty(item.id, newQty);
  };

  return (
    <>
    <motion.div
      whileHover={{ y: -4, boxShadow: '0 12px 28px rgba(0,0,0,0.08)' }}
      transition={{ type: 'spring', stiffness: 260, damping: 20 }}
      className="bg-white rounded-[20px] overflow-hidden shadow-card border border-borderbeige"
      data-testid={`food-card-${item.id}`}
    >
      <div className="relative">
        <img src={item.image_url} alt={item.name} className="w-full h-44 object-cover" loading="lazy" />
        <button
          onClick={() => setFav((f) => !f)}
          data-testid={`favorite-${item.id}`}
          className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/95 backdrop-blur flex items-center justify-center shadow-soft"
        >
          <Heart className={`w-4 h-4 transition-all ${fav ? 'fill-maroon text-maroon' : 'text-textsecondary'}`} />
        </button>
        <div className="absolute top-3 left-3 flex items-center gap-1 bg-gradient-offer px-2.5 py-1 rounded-full shadow-soft">
          <Star className="w-3 h-3 fill-textprimary text-textprimary" />
          <span className="text-xs font-heading font-semibold text-textprimary">
            {item.review_count >= 3 && item.avg_rating != null ? item.avg_rating : item.rating}
          </span>
          {item.review_count >= 3 && (
            <span className="text-[10px] text-textprimary/70 font-body">({item.review_count})</span>
          )}
        </div>
        {item.is_featured && (
          <div className="absolute bottom-3 left-3 bg-maroon text-white text-[10px] font-heading font-bold px-2 py-1 rounded-md tracking-wider">
            BESTSELLER
          </div>
        )}
      </div>

      <div className="p-4">
        <div className="flex items-start justify-between gap-2 mb-1">
          <h3 className="font-heading font-semibold text-textprimary leading-tight text-base">{item.name}</h3>
          <div className={`shrink-0 w-4 h-4 border-2 flex items-center justify-center ${item.is_veg ? 'border-fresh' : 'border-maroon'}`}>
            <div className={`w-2 h-2 rounded-full ${item.is_veg ? 'bg-fresh' : 'bg-maroon'}`} />
          </div>
        </div>
        <p className="text-xs text-textsecondary line-clamp-2 mb-3 font-body">{item.description}</p>
        <div className="flex items-center gap-3 mb-4 text-xs text-textlight font-body">
          <span className="flex items-center gap-1"><Leaf className="w-3 h-3" />{item.prep_time} min</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="font-heading font-bold text-maroon text-lg">₹{item.price}</span>
          {cartItem ? (
            <div className="flex items-center gap-2 bg-saffron rounded-2xl px-2 py-1 shadow-warm" data-testid={`qty-controls-${item.id}`}>
              <button onClick={() => handleQtyChange(cartItem.quantity - 1)} data-testid={`decrease-qty-${item.id}`} className="w-6 h-6 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center">
                <Minus className="w-3 h-3 text-white" />
              </button>
              <span className="text-white font-heading font-bold text-sm min-w-[16px] text-center">{cartItem.quantity}</span>
              <button 
                onClick={() => handleQtyChange(cartItem.quantity + 1)} 
                data-testid={`increase-qty-${item.id}`} 
                disabled={isOfflineAndNoPreorders}
                className="w-6 h-6 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Plus className="w-3 h-3 text-white" />
              </button>
            </div>
          ) : (
            <motion.button
              whileTap={{ scale: 0.95 }}
              whileHover={{ scale: 1.05 }}
              onClick={onAdd}
              disabled={isOfflineAndNoPreorders}
              data-testid={`add-to-cart-${item.id}`}
              className="bg-saffron text-white px-4 py-2 rounded-2xl font-heading font-semibold text-sm shadow-warm hover:bg-saffron-dark transition-colors flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus className="w-4 h-4" /> Add
            </motion.button>
          )}
        </div>
      </div>
    </motion.div>
    <CustomizeSheet item={item} open={sheetOpen} onClose={() => setSheetOpen(false)} />
    </>
  );
};
